import {
  APIVersionOption,
  HeadlessButtonRenderOptions,
  HeadlessUniversalCheckoutOptions,
  PaymentFlow,
  PaymentHandling,
} from '@primer-io/checkout-web'

export const enum StripeDeclineReason {
  STOLEN_CARD = 'stolen_card',
  LOST_CARD = 'lost_card',
  GENERIC_DECLINE_ERROR = 'generic_decline',
}

export const enum StripeSoftDeclineReason {
  AUTHENTICATION_FAILURE = 'payment_intent_authentication_failure',
  DO_NOT_HONOR = 'do_not_honor',
  INSUFFICIENT_FUNDS = 'insufficient_funds',
  INVALID_AMOUNT = 'invalid_amount',
  PROCESSING_ERROR = 'processing_error',
  REENTER_TRANSACTION = 'reenter_transaction',
  SERVICE_NOT_ALLOWED = 'service_not_allowed',
  TRANSACTION_NOT_ALLOWED = 'transaction_not_allowed',
  CARD_VELOCITY_EXCEEDED = 'card_velocity_exceeded',
  GENERIC_DECLINE = 'generic_decline',
  TRY_AGAIN_LATER = 'try_again_later',
}

export const STRIPE_SOFT_DECLINE_REASONS = [
  StripeSoftDeclineReason.AUTHENTICATION_FAILURE,
  StripeSoftDeclineReason.DO_NOT_HONOR,
  StripeSoftDeclineReason.INSUFFICIENT_FUNDS,
  StripeSoftDeclineReason.INVALID_AMOUNT,
  StripeSoftDeclineReason.PROCESSING_ERROR,
  StripeSoftDeclineReason.REENTER_TRANSACTION,
  StripeSoftDeclineReason.SERVICE_NOT_ALLOWED,
  StripeSoftDeclineReason.TRANSACTION_NOT_ALLOWED,
  StripeSoftDeclineReason.CARD_VELOCITY_EXCEEDED,
  StripeSoftDeclineReason.GENERIC_DECLINE,
  StripeSoftDeclineReason.TRY_AGAIN_LATER,
]

export const enum PaymentSystem {
  STRIPE = 'stripe',
  PRIMER = 'primer',
}

export const enum PaymentMethod {
  CREDIT_CARD = 'credit_card',
  APPLE_PAY = 'apple_pay',
  GOOGLE_PAY = 'google_pay',
  PAYPAL = 'pay_pal',
}

export const enum StripeErrorCode {
  CARD_DECLINED = 'card_declined',
}

export const enum CardPaymentFieldName {
  NAME = 'name',
  NUMBER = 'cardNumber',
  EXPIRY = 'cardExpiry',
  CVC = 'cardCvc',
}

export const PRIMER_PAYMENT_ERRORS = {
  INSUFFICIENT_FUNDS: 'payment.paymentErrors.insufficientFunds',
  CONNECTION_ERROR: 'payment.paymentErrors.connectionError',
  PAYMENT_FAILED: 'payment.paymentErrors.insufficientFunds',
  DECLINED: 'payment.paymentErrors.declinedError',
  AUTHENTICATION_REQUIRED: 'payment.paymentErrors.authenticationRequired',
  WITHDRAWAL_LIMIT_EXCEEDED: 'payment.paymentErrors.withdrawLimit',
  EXPIRED_CARD: 'payment.paymentErrors.expiredCard',
  COMMON_ERROR: 'payment.paymentErrors.somethingWentWrongError',
  ERROR: 'payment.paymentErrors.somethingWentWrongError',
  INVALID_CARD_NUMBER: 'payment.paymentErrors.somethingWentWrongError',
  LOST_OR_STOLEN_CARD: 'payment.paymentErrors.somethingWentWrongError',
  SUSPECTED_FRAUD: 'payment.paymentErrors.somethingWentWrongError',
  UNKNOWN: 'payment.paymentErrors.somethingWentWrongError',
  REFER_TO_CARD_ISSUER: 'payment.paymentErrors.somethingWentWrongError',
  DO_NOT_HONOR: 'payment.paymentErrors.somethingWentWrongError',
  ISSUER_TEMPORARILY_UNAVAILABLE:
    'payment.paymentErrors.somethingWentWrongError',
}

export const THREEDS_REDIRECT_SEARCH_PARAM = 'threeds'
export const EMPTY_FIELD_ERROR = `can't be blank`
export const DEFAULT_CARDHOLDER_NAME = ' '

export const PRIMER_PAY_PAL_ID = 'paypal-button'
export const PRIMER_APPLE_PAY_ID = 'primer-apple-pay-button'
export const PRIMER_GOOGLE_PAY_ID = 'primer-google-pay-button'

const FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600&display=swap'

export const PRIMER_PAYPAL_CONFIG: HeadlessButtonRenderOptions = {
  style: {
    buttonType: 'short',
    buttonColor: 'gold',
    buttonHeight: 55,
    buttonShape: 'pill',
  },
}

export const PRIMER_APPLE_PAY_CONFIG: HeadlessButtonRenderOptions = {
  style: { buttonType: 'plain', buttonStyle: 'black', buttonHeight: 55 },
}

export const PRIMER_GOOGLE_PAY_CONFIG: HeadlessButtonRenderOptions = {
  style: { buttonType: 'plain', buttonColor: 'black', buttonHeight: 55 },
}

export const PRIMER_CONFIG: Pick<
  HeadlessUniversalCheckoutOptions,
  'card' | 'paypal' | 'style' | 'paymentHandling' | 'apiVersion'
> = {
  card: { cardholderName: { required: false } },
  style: { stylesheets: [{ href: FONTS_URL }] },
  paymentHandling: 'MANUAL' as PaymentHandling,
  paypal: { paymentFlow: PaymentFlow.PREFER_VAULT },
  apiVersion: '2.4' as APIVersionOption,
}

export const STRIPE_ELEMENTS_OPTIONS = { fonts: [{ cssSrc: FONTS_URL }] }
